/**
 * Instalación de la app desde el navegador (Chrome/Edge en Android): el navegador avisa con `beforeinstallprompt` que se puede
 * instalar y entrega un objeto para mostrar el cuadro de instalación cuando se quiera. Hay que escucharlo desde el principio (puede
 * llegar antes de que se dibuje la pantalla), así que `captureInstallPrompt()` se llama al arrancar.
 *
 * iOS (Safari) NO tiene esto: no se puede instalar desde código, solo con "Agregar a inicio" del menú compartir.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
let installed = false;
let captured = false;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((fn) => fn());

function onBeforeInstallPrompt(event: Event): void {
  event.preventDefault(); // sin esto el navegador muestra su propio cartel y perdemos el control del botón
  deferred = event as BeforeInstallPromptEvent;
  notify();
}

function onAppInstalled(): void {
  deferred = null;
  installed = true;
  notify();
}

export function captureInstallPrompt(): void {
  if (captured || typeof window === 'undefined') return;
  captured = true;
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
  window.addEventListener('appinstalled', onAppInstalled);
}

/** ¿El navegador ofrece instalar la app con un toque (cuadro nativo)? */
export function canPromptInstall(): boolean {
  return deferred !== null;
}

/** ¿Se instaló durante esta visita? */
export function wasInstalledNow(): boolean {
  return installed;
}

export type InstallResult = 'accepted' | 'dismissed' | 'unavailable';

/** Muestra el cuadro de instalación del navegador. `unavailable` = no hay (iOS, navegador sin soporte, ya instalada): usar las instrucciones manuales. */
export async function promptInstall(): Promise<InstallResult> {
  const event = deferred;
  if (!event) return 'unavailable';
  deferred = null; // el evento solo sirve una vez
  notify();
  try {
    await event.prompt();
    const { outcome } = await event.userChoice;
    return outcome;
  } catch {
    return 'unavailable';
  }
}

/** Para que la pantalla se vuelva a dibujar cuando llega el evento o se instala. Devuelve cómo dejar de escuchar. */
export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * ¿La app ya está instalada en este celular? Solo lo sabe Chrome en Android (`getInstalledRelatedApps`, que además necesita
 * `related_applications` en el manifest); en el resto devuelve `false` (= "no se sabe", no "no está instalada").
 */
export async function detectInstalledApp(): Promise<boolean> {
  try {
    const nav = navigator as Navigator & { getInstalledRelatedApps?: () => Promise<unknown[]> };
    if (!nav.getInstalledRelatedApps) return false;
    return (await nav.getInstalledRelatedApps()).length > 0;
  } catch {
    return false;
  }
}

/** Solo para los tests. */
export function resetInstallPromptForTests(): void {
  if (typeof window !== 'undefined') {
    window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.removeEventListener('appinstalled', onAppInstalled);
  }
  deferred = null;
  installed = false;
  captured = false;
  listeners.clear();
}
